import React, {useEffect, useState} from "react"
import {
	Box,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	TextField,
	Typography,
} from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import {DatePicker} from "@mui/x-date-pickers/DatePicker"
import {LocalizationProvider} from "@mui/x-date-pickers"
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns"
import {Link as RouterLink, useSearchParams} from "react-router-dom"
import styled from "styled-components"
import {differenceInMinutes, format, isValid, startOfDay} from "date-fns"

import {DeferralReasonChip, HintChip, phraseDeferralReason} from "./UnidentifiedInbox/components/chips"
import {ConversationIDLine} from "./UnidentifiedInbox/components/ConversationIDLine"

import {api, UnidentifiedInboxMessage} from "@indebted/api"
import {BackToSearch} from "@indebted/components/BackToSearch"
import {Layout} from "@indebted/components/Layout"
import {colors} from "@indebted/theme/colors"

const PAGE_SIZE = 20

type InboxState = {
	loading: boolean
	messages: UnidentifiedInboxMessage[]
	total: number
	mailboxes: string[]
	reasons: string[]
	error: string
}

const emptyInbox: InboxState = {loading: true, messages: [], total: 0, mailboxes: [], reasons: [], error: ""}

const InboxCardFrame = styled.div`
	border-radius: 8px;
	background-color: ${colors.primary.white};
	border: 1px solid transparent;
	box-shadow:
		0px 3px 1px -2px rgba(0, 0, 0, 0.2),
		0px 2px 2px 0px rgba(0, 0, 0, 0.14),
		0px 1px 5px 0px rgba(0, 0, 0, 0.12);
	transition: box-shadow 0.2s;

	&:hover {
		box-shadow:
			0px 3px 3px -2px rgba(66, 103, 178, 0.2),
			0px 4px 6px 0px rgba(66, 103, 178, 0.14),
			0px 2px 8px 1px rgba(66, 103, 178, 0.12);
	}
`

const InboxCardLink = styled(RouterLink)`
	display: block;
	padding: 16px;
	text-decoration: none;
	color: inherit;
`

function UnidentifiedInboxPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1)
	const mailbox = searchParams.get("mailbox") ?? ""
	const reason = searchParams.get("reason") ?? ""
	const from = searchParams.get("from") ?? ""
	const conversation = searchParams.get("conversation") ?? ""
	const [inbox, setInbox] = useState<InboxState>(emptyInbox)

	useEffect(() => {
		let showing = true
		setInbox((previous) => ({...previous, loading: true, error: ""}))
		api.inbox
			.listUnidentified({limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, mailbox, reason, from, conversation})
			.then((answer) => {
				if (!showing) return
				setInbox({
					loading: false,
					messages: answer.Messages,
					total: answer.Total,
					mailboxes: answer.Mailboxes,
					reasons: answer.DeferralReasons,
					error: "",
				})
			})
			.catch((failure) => {
				if (!showing) return
				setInbox((previous) => ({
					loading: false,
					messages: [],
					total: previous.total,
					mailboxes: previous.mailboxes,
					reasons: previous.reasons,
					error: failure?.Message ?? "The inbox could not be read",
				}))
			})

		return () => {
			showing = false
		}
	}, [page, mailbox, reason, from, conversation])

	const chooseFilter = (name: string, value: string) => {
		const next = new URLSearchParams(searchParams)
		if (value === "") {
			next.delete(name)
		} else {
			next.set(name, value)
		}
		next.delete("page")
		setSearchParams(next)
	}

	// The route bounds on an instant, so the picked day is sent as the moment it
	// began here rather than as a date: the agent asking for the 4th means their
	// own 4th, which is not the one a bare date would be read as server-side.
	// A day the calendar does not have — the 31st of February — arrives as an
	// invalid date, which has no instant to convert and would throw on the way out.
	const chooseDeferredFrom = (picked: Date | null) => {
		if (picked !== null && !isValid(picked)) return
		chooseFilter("from", picked === null ? "" : startOfDay(picked).toISOString())
	}

	const setPage = (newPage: number) => {
		const next = new URLSearchParams(searchParams)
		if (newPage <= 1) {
			next.delete("page")
		} else {
			next.set("page", String(newPage))
		}
		setSearchParams(next)
		window.scrollTo({top: 0, behavior: "smooth"})
	}

	const totalPages = Math.max(1, Math.ceil(inbox.total / PAGE_SIZE))
	const pager = inbox.total > PAGE_SIZE && (
		<Pagination
			count={totalPages}
			page={Math.min(page, totalPages)}
			onChange={(_, value) => setPage(value)}
			color="primary"
			size="small"
		/>
	)

	return (
		<Layout>
			<Box px={3} pt={3} mb={2}>
				<BackToSearch backURL="/" />
			</Box>
			<Box px={3} pb={4}>
				<Typography variant="h6" mb={2}>
					Unidentified inbox
				</Typography>

				<Box display="flex" gap={2} mb={2} flexWrap="wrap">
					<FormControl size="small" sx={{minWidth: 240}}>
						<InputLabel shrink id="inbox-mailbox-filter">
							Mailbox
						</InputLabel>
						<Select
							labelId="inbox-mailbox-filter"
							label="Mailbox"
							displayEmpty
							value={mailbox}
							onChange={(event) => chooseFilter("mailbox", event.target.value)}
						>
							<MenuItem value="">All mailboxes</MenuItem>
							{inbox.mailboxes.map((named) => (
								<MenuItem key={named} value={named}>
									{named}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{minWidth: 240}}>
						<InputLabel shrink id="inbox-reason-filter">
							Reason
						</InputLabel>
						<Select
							labelId="inbox-reason-filter"
							label="Reason"
							displayEmpty
							value={reason}
							onChange={(event) => chooseFilter("reason", event.target.value)}
						>
							<MenuItem value="">All reasons</MenuItem>
							{inbox.reasons.map((named) => (
								<MenuItem key={named} value={named}>
									{phraseDeferralReason(named)}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<ConversationFilter
						conversation={conversation}
						choose={(value) => chooseFilter("conversation", value)}
					/>

					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DatePicker
							label="Deferred from"
							value={from === "" ? null : new Date(from)}
							onChange={chooseDeferredFrom}
							format="dd MMM yyyy"
							slotProps={{
								field: {clearable: true},
								textField: {size: "small", sx: {minWidth: 240}},
							}}
						/>
					</LocalizationProvider>
				</Box>

				{inbox.loading && <CircularProgress size={24} />}

				{!inbox.loading && inbox.error !== "" && <Typography color="error">{inbox.error}</Typography>}

				{!inbox.loading && inbox.error === "" && inbox.total > 0 && (
					<Typography color="text.secondary" mb={1}>
						{inbox.total} waiting for identification
					</Typography>
				)}

				{!inbox.loading && <Box mb={1}>{pager}</Box>}

				{!inbox.loading && inbox.error === "" && inbox.messages.length === 0 && (
					<Typography color="text.secondary">
						{inbox.total === 0 ? "No messages are waiting for identification" : "No messages on this page"}
					</Typography>
				)}

				<Box display="flex" flexDirection="column" gap={1}>
					{!inbox.loading &&
						inbox.error === "" &&
						inbox.messages.map((message) => (
							<InboxCardFrame key={`${message.ConversationID}:${message.ProviderMessageID}`}>
								<InboxCardLink to={itemView(message, searchParams.toString())}>
									<Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
										<DeferralReasonChip reason={message.DeferralReason} />
										{(message.Hints ?? []).map((hint) => (
											<HintChip key={`${hint.Kind}:${hint.Label}`} hint={hint} />
										))}
										{message.Mailbox !== "" && (
											<Chip size="small" variant="outlined" label={message.Mailbox} />
										)}
										<Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
											<AccessTimeIcon fontSize="small" />
											<Typography
												variant="body2"
												color="text.secondary"
												title={format(new Date(message.DeferredAt), "d MMM yyyy HH:mm")}
											>
												Waiting {phraseWait(message.DeferredAt)}
											</Typography>
										</Box>
									</Box>
									<Typography variant="body2" color="text.secondary" mb={0.5}>
										{sender(message)}
									</Typography>
									<Typography variant="subtitle1" fontWeight={600}>
										{message.Subject || "(no subject)"}
									</Typography>
								</InboxCardLink>
								<Box px={2} pb={2} mt={-1} display="flex">
									<ConversationIDLine conversationID={message.ConversationID} />
								</Box>
							</InboxCardFrame>
						))}
				</Box>

				{!inbox.loading && <Box mt={2}>{pager}</Box>}
			</Box>
		</Layout>
	)
}

// The field holds a draft and commits on Enter alone: an ID arrives whole
// (pasted from Genesys, the lake, a thread), so a per-key commit would fire a
// request per character — and a blur commit would swallow the very click that
// blurred it, the refetch unmounting the card under the pointer.
function ConversationFilter({conversation, choose}: {conversation: string; choose: (value: string) => void}) {
	const [draft, setDraft] = useState(conversation)

	// A change arriving through the address (back navigation, a shared link)
	// replaces the draft: the address is the record, the draft only a way there.
	useEffect(() => {
		setDraft(conversation)
	}, [conversation])

	const commit = () => {
		const entered = draft.trim()
		setDraft(entered)
		if (entered !== conversation) choose(entered)
	}

	return (
		<TextField
			label="Conversation ID"
			size="small"
			sx={{minWidth: 240}}
			slotProps={{inputLabel: {shrink: true}}}
			value={draft}
			onChange={(event) => setDraft(event.target.value)}
			onKeyDown={(event) => {
				if (event.key === "Enter") commit()
			}}
		/>
	)
}

function itemView(message: UnidentifiedInboxMessage, listSearch: string) {
	// The list serves email only, so the channel segment is fixed until SMS gets its own inbox.
	const item = `/inbox/email/${message.ConversationID}/${encodeURIComponent(message.ProviderMessageID)}`
	// Carries the page and filters the agent is reading from, so returning lands
	// them back where they left rather than at the head of an unfiltered queue.
	const back = `/inbox/unidentified${listSearch === "" ? "" : `?${listSearch}`}`

	return `${item}?back=${encodeURIComponent(back)}`
}

// A fuzzed duration stops discriminating at the queue's oldest end: two entries
// fifty and fifty-nine hours old both read "2 days". Days carry their spare
// hours so that end stays ordered to the eye.
function phraseWait(deferredAt: string) {
	const minutes = Math.max(0, differenceInMinutes(new Date(), new Date(deferredAt)))
	if (minutes < 60) return counted(minutes, "minute")

	const hours = Math.floor(minutes / 60)
	if (hours < 24) return counted(hours, "hour")

	const days = Math.floor(hours / 24)
	const spareHours = hours % 24

	return spareHours === 0 ? counted(days, "day") : `${counted(days, "day")} ${counted(spareHours, "hour")}`
}

function counted(n: number, noun: string) {
	return `${n} ${noun}${n === 1 ? "" : "s"}`
}

function sender(message: UnidentifiedInboxMessage) {
	return message.FromLabel === "" ? message.FromAddress : `${message.FromLabel} <${message.FromAddress}>`
}

export {UnidentifiedInboxPage}
